import { computed, watch, type Ref } from 'vue'
import { sdk } from '@/graphql/client'
import { useAuthStore } from '@/stores/auth'
import { useAsyncData } from '@lib/composables/useAsyncData'
import { useCursorPagination } from '@lib/composables/useCursorPagination'

export interface TicketFilters {
    search: string
    event: string | null
    ticketset: string | null
    used: boolean | null
    code: string
    email: string
    firstName: string
    lastName: string
    orderBy: string | null
}

export function useTicketsList(filters: Ref<TicketFilters>) {
    const auth = useAuthStore()
    const pagination = useCursorPagination({ defaultPageSize: 50 })

    const result = useAsyncData(
        async () => {
            const r = await sdk.AdminTickets({
                first: pagination.pageSize.value,
                after: pagination.cursor.value,
                event: filters.value.event ?? undefined,
                ticketset: filters.value.ticketset ?? undefined,
                used: filters.value.used ?? undefined,
                search: filters.value.search || undefined,
                code: filters.value.code || undefined,
                email: filters.value.email || undefined,
                firstName: filters.value.firstName || undefined,
                lastName: filters.value.lastName || undefined,
                orderBy: filters.value.orderBy ?? undefined
            })
            const conn = r.admin?.ticket?.list
            if (conn?.pageInfo) {
                pagination.applyResult({
                    pageInfo: conn.pageInfo,
                    totalCount: conn.totalCount,
                    itemCount: conn.edges?.length ?? 0
                })
            }
            return conn
        },
        {
            watch: [
                pagination.cursor,
                pagination.pageSize,
                computed(() => auth.invalidationKey) // refetch po changeInstance
            ]
        }
    )

    // refetch on filter change (resets cursor)
    watch(
        filters,
        () => {
            pagination.reset()
            result.refetch()
        },
        { deep: true }
    )

    const tickets = computed(() =>
        (result.data.value?.edges ?? []).map(e => e?.node).filter((n): n is NonNullable<typeof n> => n != null)
    )

    return { ...result, tickets, pagination }
}
